import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  ContentBox,
  ContentPage,
  DropdownAction,
  FullTable,
  NoResults,
  Notice,
  PageActions,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { DomainStatus, escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import { apiKeyScopeLabel, apiKeyStatus, externalDocsUrl } from "@helpers/apiKeys";
import ApiKeyActionModal from "./ApiKeyActionModal";
import ApiKeyFormModal from "./ApiKeyFormModal";
import ApiKeySecretModal from "./ApiKeySecretModal";

// Chaves de API da empresa (modelo Lista do DS OneWork). Só ADM (guard da rota).
// O back guarda só o hash: o texto da chave aparece uma vez, ao gerar ou rotacionar.

const PAGE_SIZE = 25;

const BREADCRUMB = [{ page: "Chaves de API", route: "/c/api-keys" }];

const NoticeSpace = styled.div`
  margin-bottom: 20px;
`;

const EMPTY = { totalRegisters: 0, list: [], totalPages: 1 };

function openDocs() {
  window.open(externalDocsUrl(), "_blank", "noopener,noreferrer");
}

function createdText(key) {
  const when = key.created_at ? `em ${formatDateTime(key.created_at)}` : "";
  return [key.createdBy?.name ? `Criada por ${key.createdBy.name}` : "Criada", when].filter(Boolean).join(" ");
}

export default function ApiKeys() {
  const apiKeysService = useService("apiKeys");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Só a resposta da última requisição vale (trocas de página seguidas podem voltar fora de ordem)
  const lastRequest = useRef(0);
  // { mode: "create" | "rotate" | "revoke", apiKey }
  const [modal, setModal] = useState(null);
  const [plaintext, setPlaintext] = useState(null);

  usePageHeader({ title: "Chaves de API", breadcrumb: BREADCRUMB });

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize]);

  async function fetchKeys() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await apiKeysService.list(`page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}`);
      if (request !== lastRequest.current) return;
      if (!result?.data) throw new Error("Erro ao carregar as chaves");
      setPaginationData(result.data);
    } catch (err) {
      if (request !== lastRequest.current) return;
      toast.error(err?.response?.data?.msg || err?.message || "Erro ao carregar as chaves");
      setPaginationData(EMPTY);
    }
    setLoading(false);
  }

  const openCreate = () => setModal({ mode: "create" });
  const closeModal = () => setModal(null);

  function afterChange(newPlaintext) {
    setModal(null);
    if (newPlaintext) setPlaintext(newPlaintext);
    fetchKeys();
  }

  const columns = [
    {
      id: "name",
      header: "Nome",
      size: 420,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.name)} $variant="paragraph_small_bold" $color="N900" />
          <Text $text={escapeHtml(createdText(row.original))} $variant="caption_medium" $color="N400" />
        </div>
      ),
    },
    { id: "prefix", header: "Prefixo", size: 160, cell: ({ row }) => `${row.original.prefix}…` },
    { id: "scope", header: "Escopo", size: 130, cell: ({ row }) => apiKeyScopeLabel(row.original.scope) },
    {
      id: "last_used_at",
      header: "Último uso",
      size: 160,
      cell: ({ row }) => (row.original.last_used_at ? formatDateTime(row.original.last_used_at) : "—"),
    },
    {
      id: "expires_at",
      header: "Expira em",
      size: 160,
      cell: ({ row }) => (row.original.expires_at ? formatDateTime(row.original.expires_at) : "Nunca"),
    },
    {
      id: "status",
      header: "Status",
      size: 90,
      cell: ({ row }) => <DomainStatus domain="chaveApi" value={apiKeyStatus(row.original)} />,
    },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => {
        const status = apiKeyStatus(row.original);
        if (status === "revogada") return null;
        return (
          <DropdownAction
            options={[
              // Na expirada o back recusa a rotação: a saída é gerar uma chave nova
              {
                label: "Rotacionar",
                action: () => setModal({ mode: "rotate", apiKey: row.original }),
                show: status === "ativa",
              },
              { label: "Revogar", action: () => setModal({ mode: "revoke", apiKey: row.original }), isImportant: true },
            ]}
          />
        );
      },
    },
  ];

  return (
    <ContentPage>
      <PageActions>
        <Button $text="Documentação da API" $variant="outline" $action={openDocs} />
        <Button $text="Nova chave" $variant="primary" $action={openCreate} />
      </PageActions>

      <ContentBox>
        <NoticeSpace>
          <Notice
            $variant="gray"
            $text="Gere chaves para que sistemas externos consumam dados da sua empresa via API. A chave gerada é exibida apenas uma vez: guarde-a em local seguro."
          />
        </NoticeSpace>

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          noResults={
            <NoResults
              notResultText="Nenhuma chave por aqui"
              includeText="Nova chave"
              includeAction={openCreate}
              includeActions={<ButtonLink $text="Documentação da API" $action={openDocs} />}
            />
          }
        />
      </ContentBox>

      {modal?.mode === "create" ? <ApiKeyFormModal onClose={closeModal} onCreated={afterChange} /> : null}
      {modal?.mode === "rotate" || modal?.mode === "revoke" ? (
        <ApiKeyActionModal mode={modal.mode} apiKey={modal.apiKey} onClose={closeModal} onDone={afterChange} />
      ) : null}
      {plaintext ? <ApiKeySecretModal plaintext={plaintext} onClose={() => setPlaintext(null)} /> : null}
    </ContentPage>
  );
}
